import {AfterViewInit, Component, computed, effect, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {Subscription} from 'rxjs';
import {HttpEvent, HttpEventType, HttpResponse} from '@angular/common/http';
import {SlideShowComponent} from '../../shared/components/slide-show/slide-show/slide-show.component';
import {ModalService} from '../../services/modal.service';
import {AudioService, AudioTrack} from '../../services/audio/audio.service';
import {MediaItem, MediaTimingService} from '../../services/media-timing.service';
import {FileUploadService} from '../../services/file-upload.service';
import {ApiIntegrationService} from '../../services/api-integration.service';
import {SlideshowDataService} from '../../services/slide-show/slideshow-data.service';
import {SlideshowStateService} from '../../services/slide-show/slideshow-state.service';
import {SearchVisualsData, SearchVisualsResponse, GamingResponse, hasGamingSongs} from '../../services/models/api-response.models';
import {ZKeyDetectorService} from '../../services/z-key-detector.service';
import {LeadGenService} from '../../services/lead-gen.service';

@Component({
  selector: 'app-live-demo',
  imports: [
    FormsModule,
    CommonModule,
    SlideShowComponent
  ],
  templateUrl: './live-demo.component.html',
  styleUrls: ['./live-demo.component.scss'],
  standalone: true
})
export class LiveDemoComponent implements OnDestroy, AfterViewInit {
  // Mobile detection
  isMobile = window.innerWidth < 768;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  // Tab control
  textPrompt: string = '';
  gameContext: string = '';
  // API integration properties - computed signals for reactive updates
  showUploadProgress = computed(() => this.apiIntegrationService.getShowUploadProgress());
  uploadProgress = computed(() => this.apiIntegrationService.getUploadProgress());
  // Computed properties that reference the slideshow data service
  showSlideshowModal = computed(() => this.slideshowDataService.isVisible());
  slideshowImages = computed(() => this.slideshowDataService.slideshowImages());
  audioResults = computed(() => this.slideshowDataService.audioResults());
  briefDescription = computed(() => this.slideshowDataService.briefDescription());
  resultsAvailable = computed(() => this.slideshowDataService.resultsAvailable());
  fullApiAnalyzeResponse = computed(() => this.slideshowDataService.fullApiAnalyzeResponse());

  // Enhanced slideshow media items computed from slideshow service data
  enhancedSlideshowMediaItems = computed(() => {
    const images = this.slideshowImages();
    const mediaItems = this.slideshowDataService.slideshowMediaItems();

    // If we have typed media items, use those (preferred)
    if (mediaItems.length > 0) {
      return mediaItems.map(item => ({
        url: item.url,
        type: item.type as 'image' | 'video',
        // Pass the real measured video length in seconds (undefined when
        // unknown). The slideshow re-derives audio tracks from this on open
        // and rematch, so it must carry the same unit the timing service
        // expects. The old 30000 (milliseconds) was a unit bug masked by the
        // previous 30s cap.
        duration: item.type === 'video' ? item.duration : undefined
      }));
    }

    // Otherwise, convert images to media items. No File is available on this
    // URL-only path, so the real video length is unknown; leave duration
    // undefined and let the timing service apply its 30s fallback (feeding
    // the raw 30000ms here would drive the uncapped override to never stop).
    return images.map(url => ({
      url: url,
      type: (url.includes('.mp4') || url.includes('.mov') || url.includes('.avi')) ? 'video' as const : 'image' as const,
      duration: undefined
    }));
  });
  // Subscriptions to be cleaned up on destroy
  private subscriptions: Subscription[] = [];

  constructor(
    private modalService: ModalService,
    public audioService: AudioService,
    private mediaTimingService: MediaTimingService,
    private fileUploadService: FileUploadService,
    private apiIntegrationService: ApiIntegrationService,
    private slideshowDataService: SlideshowDataService,
    private slideshowStateService: SlideshowStateService,
    public zKeyDetectorService: ZKeyDetectorService,
    private leadGenService: LeadGenService
  ) {
    // Track previous slideshow visibility to detect close transition
    let prevVisible = false;
    effect(() => {
      const isVisible = this.slideshowDataService.isVisible();
      const hasResults = this.slideshowDataService.resultsAvailable();
      // Detect transition from visible to hidden while results exist
      if (prevVisible && !isVisible && hasResults) {
        if (this.leadGenService.shouldShowPopup()) {
          this.modalService.showLeadGen();
        }
      }
      prevVisible = isVisible;
    });
  }

  // API analyzing state delegated to service
  get isAnalyzing(): boolean {
    return this.apiIntegrationService.isAnalyzing();
  }

  // Gaming mode state from z-key detector service
  get isGamingMode(): boolean {
    return this.zKeyDetectorService.isGamingMode();
  }

  // File handling delegated to FileUploadService - exposing getters for template
  get selectedFiles(): File[] {
    return this.fileUploadService.getSelectedFiles();
  }

  get previewItems(): { url: string; type: 'image' | 'video'; duration?: number }[] {
    return this.fileUploadService.getPreviewItems();
  }

  get isDragging(): boolean {
    return this.fileUploadService.isDragging();
  }

  get defaultVideoPlaceholderSvg(): string {
    return this.fileUploadService.defaultVideoPlaceholderSvg;
  }

  get totalUploadSize(): number {
    return this.fileUploadService.getTotalUploadSize();
  }

  get isCompressingFiles(): boolean {
    return this.fileUploadService.isCompressingFiles();
  }

  get showMobileCaptureOptions(): boolean {
    return this.fileUploadService.getShowMobileCaptureOptions();
  }

  get currentMediaType(): 'image' | 'video' | 'any' {
    return this.fileUploadService.getCurrentMediaType();
  }

  get isMobileDeleteMode(): boolean {
    return this.fileUploadService.getIsMobileDeleteMode();
  }

  // File size constants for template access
  get TOTAL_SIZE_LIMIT(): number {
    return 100 * 1024 * 1024;
  } // 100MB

  /**
   * Initialize file input reference after view initialization
   */
  ngAfterViewInit(): void {
    // Set the file input reference in the FileUploadService
    this.fileUploadService.setFileInputRef(this.fileInput);
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Delegate cleanup to services
    this.fileUploadService.cleanup();
    this.apiIntegrationService.cleanup();
  }

  // File upload methods - All delegated to FileUploadService

  triggerFileInput(): void {
    if (this.isAnalyzing) {
      return;
    }
    this.fileUploadService.triggerFileInput();
  }

  onFilesSelected(event: Event): void {
    this.fileUploadService.onFilesSelected(event);
  }

  onDragOver(event: DragEvent): void {
    this.fileUploadService.onDragOver(event);
  }

  onDragLeave(event: DragEvent): void {
    if (this.isAnalyzing) {
      return;
    }
    this.fileUploadService.onDragLeave(event);
  }

  onDrop(event: DragEvent): void {
    if (this.isAnalyzing) {
      return;
    }
    this.fileUploadService.onDrop(event);
  }

  removeFile(index: number): void {
    // Reset results availability when files change
    this.slideshowDataService.setResultsAvailable(false);

    // Clear audio when files are removed to prevent old songs from continuing to play
    this.audioService.stop(true);
    this.audioService.cleanup();

    // Delegate to service
    this.fileUploadService.removeFile(index);
  }


  /**
   * Remove all uploaded files and clear associated state
   */
  removeAllFiles(): void {
    // Reset results availability when files change
    this.slideshowDataService.setResultsAvailable(false);

    // Clear audio when files are removed to prevent old songs from continuing to play
    this.audioService.stop(true);
    this.audioService.cleanup();

    // Reset file upload service to clear all files
    this.fileUploadService.reset();
  }

  /**
   * Handle mobile capture option selection
   */
  selectMobileCaptureOption(option: 'camera-photo' | 'camera-video' | 'gallery'): void {
    this.fileUploadService.selectMobileCaptureOption(option);
  }

  /**
   * Cancel mobile capture options
   */
  cancelMobileCaptureOptions(): void {
    this.fileUploadService.cancelMobileCaptureOptions();
  }

  /**
   * Toggle mobile delete mode
   */
  toggleMobileDeleteMode(): void {
    this.fileUploadService.toggleMobileDeleteMode();
  }

  /**
   * Main analyze method - initiates the analysis of selected files
   * @param isRetry Whether this is a retry attempt after login
   */
  analyze(isRetry: boolean = false): void {
    // If we already have results and this is not a retry, reset only results (keep uploaded files)
    // This makes clicking "Rematch" clear previous results but preserve user's uploaded files
    if (!isRetry && this.resultsAvailable()) {
      console.log('🔄 Re-matching detected - resetting results while preserving uploaded files');
      this.performResultsReset();
    }

    // Reset previous response data to ensure we don't use stale prompts
    this.slideshowDataService.setFullApiResponse(null);
    this.slideshowDataService.setCurrentAlternativePromptIndex(0);

    // Validate file selection
    if (this.selectedFiles.length === 0) {
      this.modalService.showErrorMessage({message: 'Please select at least one file to analyze.'});
      return;
    }

    // Determine if gaming mode is active and pass game context
    const isGamingModeActive = this.isGamingMode;
    const gameContext = isGamingModeActive ? this.gameContext : undefined;

    // Call the API service with complete delegation
    const subscription = this.apiIntegrationService.analyzeFiles(this.selectedFiles, this.textPrompt, isRetry, gameContext)
      .subscribe({
        next: (event: HttpEvent<SearchVisualsResponse | GamingResponse>) => {
          // Only handle Response events for success
          if (event.type === HttpEventType.Response) {
            const response = (event as HttpResponse<SearchVisualsResponse | GamingResponse>).body;

            if (gameContext && response && 'data' in response && response.data && 'results' in response.data) {
              // Gaming mode response
              console.log('🎮 Detected gaming mode response, calling handleGamingResponse');
              this.handleGamingResponse(response as GamingResponse, isGamingModeActive, gameContext);
            } else if (response && 'data' in response && response.data && 'results' in response.data) {
              // Normal mode response
              console.log('🔍 Detected normal mode response, calling handleSuccessfulResponse');
              this.handleSuccessfulResponse((response as SearchVisualsResponse).data, response as SearchVisualsResponse, isRetry);
            } else {
              console.error('❌ Unknown response structure:', response);
            }
          }
        },
        error: (error) => {
          // Handle no matches error specifically
          if (error.message === 'NO_MATCHES_FOUND') {
            this.modalService.showNoMatches();
          } else {
            console.error('Analysis failed:', error);
          }
        }
      });

    this.subscriptions.push(subscription);
  }

  /**
   * Re-open the slideshow modal to view results again
   */
  reopenSlideshow(): void {
    if (this.resultsAvailable() && this.audioResults().length > 0) {
      this.slideshowDataService.setVisible(true);
    }
  }

  /**
   * Handle enhanced slideshow visibility changes
   */
  onSlideshowVisibilityChange(isVisible: boolean): void {
    this.slideshowDataService.setVisible(isVisible);
  }

  /**
   * Handle gaming mode response from contextual API
   * @param response The gaming response from contextual API
   * @param isGamingMode Whether gaming mode is active
   * @param gameContext The game context used
   */
  handleGamingResponse(response: GamingResponse, isGamingMode: boolean, gameContext: string): void {
    console.log('Gaming mode response received:', response);

    if (hasGamingSongs(response)) {
      // Store gaming mode state in slideshow service
      this.slideshowDataService.setGamingMode(isGamingMode);
      this.slideshowDataService.setGameContext(gameContext);

      // Store brief description from gaming response
      this.slideshowDataService.setBriefDescription(response.data.brief || '');

      // Convert gaming songs to AudioTrack format
      const audioTracks: AudioTrack[] = response.data.results.map((song: any) => {
        // Gaming mode: play entire song from start to natural end
        return {
          url: song.url,
          title: song.title || 'Unknown Title',
          artist: song.artist || 'Unknown Artist',
          startTime: 0, // Start from beginning
          endTime: undefined, // Explicitly undefined = play until natural end
          briefDescription: response.data.brief || ''
        };
      });

      // Set tracks in audio service
      this.audioService.setTracks(audioTracks);

      // Force recreate previews
      this.fileUploadService.createPreviews();
      const previewUrls = this.fileUploadService.getPreviewUrls();

      if (previewUrls.length === 0) {
        this.slideshowDataService.setSlideshowImages([this.defaultVideoPlaceholderSvg]);
      } else {
        this.slideshowDataService.setSlideshowImages([...previewUrls]);
      }

      // Open slideshow with gaming mode data
      this.slideshowDataService.openSlideshow({
        audioResults: response.data.results,
        briefDescription: response.data.brief || '',
        slideshowImages: this.slideshowImages(),
        slideshowMediaItems: this.previewItems,
        isGamingMode: isGamingMode,
        gameContext: gameContext
      });

      // Results are available
      this.slideshowDataService.setResultsAvailable(true);
    } else {
      // No gaming songs found
      console.log('No gaming songs found in response');
      this.modalService.showNoMatches();
    }
  }

  /**
   * Handle successful response from the API
   * @param data The data portion of the API response
   * @param fullResponse Optional full API response object
   * @param isRetry Whether this is a retry attempt
   */
  handleSuccessfulResponse(data: SearchVisualsData, fullResponse?: SearchVisualsResponse, isRetry: boolean = false): void {
    // Store the full API response for later use with alternative prompts
    this.slideshowDataService.setFullApiResponse(fullResponse || {data});
    console.log('Full API response stored:', this.fullApiAnalyzeResponse());

    // Reset the alternative prompt index to start from first alternative
    this.slideshowDataService.setCurrentAlternativePromptIndex(0);

    // Store brief description if available
    this.slideshowDataService.setBriefDescription(data.brief || '');
    console.log('Brief description:', this.briefDescription());

    // ✅ CORRECT ORDER: Check empty results FIRST (per flows.md)
    if (data.results && data.results.length > 0) {
      // Analyzing state is now managed by ApiIntegrationService
      // Store audio results
      this.slideshowDataService.setAudioResults(data.results);
      console.log('Audio results:', this.audioResults());

      // Convert API results to AudioTrack format for the audio service
      const audioTracks: AudioTrack[] = data.results.map((result: any) => {
        const startTime = result.start_time || 0;

        // Convert preview items to the format expected by MediaTimingService
        const mediaItems: MediaItem[] = this.previewItems.map(item => ({
          type: item.type,
          url: item.url,
          // Use the real measured video length when known; fall back to 30s.
          duration: item.type === 'video' ? (item.duration ?? 30) : undefined
        }));

        // Calculate end time using the shared service
        const endTime = this.mediaTimingService.calculateEndTime(
          mediaItems,
          startTime,
          result.end_time
        );

        return {
          url: result.url,
          title: result.title || 'Unknown Title',
          artist: result.artist || 'Unknown Artist',
          startTime: startTime,
          endTime: endTime,
          briefDescription: this.briefDescription()
        };
      });

      // Set tracks in the audio service
      this.audioService.setTracks(audioTracks);

      // Force recreate the preview URLs to ensure they're available
      this.fileUploadService.createPreviews();

      // Get preview URLs for slideshow
      const previewUrls = this.fileUploadService.getPreviewUrls();

      // If no preview URLs were created, use placeholder
      if (previewUrls.length === 0) {
        console.log('No previews available, using placeholder');
        this.slideshowDataService.setSlideshowImages([this.defaultVideoPlaceholderSvg]);
      } else {
        // Store the preview URLs in the slideshow images array
        this.slideshowDataService.setSlideshowImages([...previewUrls]);
      }
      console.log('Slideshow images:', this.slideshowImages());

      // Open slideshow with all the data
      this.slideshowDataService.openSlideshow({
        audioResults: data.results,
        briefDescription: this.briefDescription(),
        slideshowImages: this.slideshowImages(),
        slideshowMediaItems: this.previewItems
      });

      // Results are now available to view (for reopening later)
      this.slideshowDataService.setResultsAvailable(true);
    } else {
      // ✅ EMPTY RESULTS HANDLING (per flows.md)
      console.log('Empty results received');

      if (!isRetry) {
        // ✅ FIRST ATTEMPT - EMPTY RESULTS: Auto-retry (per flows.md)
        console.log('First attempt returned empty results, retrying automatically...');
        // Note: Progress continues running (managed by ApiIntegrationService)
        this.analyze(true); // Retry with isRetry = true
        return;
      } else {
        // ✅ SECOND ATTEMPT ALSO EMPTY: Show modal (per flows.md)
        console.log('Retry also returned empty results, showing no-match modal');
        this.modalService.showNoMatches();
        return;
      }
    }

    // ✅ CORRECT ORDER: Check error message AFTER empty results (per flows.md)
    if (fullResponse && fullResponse.error && fullResponse.error.message.includes('We could not find good matches for your query. Please try running your search again.')) {
      // Show no matches modal - user can try alternative prompts via the slideshow
      this.modalService.showNoMatches();
      return;
    }
  }

  /**
   * Perform a complete reset of all slideshow and audio state INCLUDING uploaded files
   * This is for a complete fresh start (like page reload) - use performResultsReset() for rematch
   */
  private performCompleteReset(): void {
    console.log('🔄 Performing complete slideshow reset...');

    // 1. Stop and cleanup audio completely
    this.audioService.stop(true); // Force stop with cleanup
    this.audioService.cleanup();

    // 2. Close any open slideshow modal immediately
    this.slideshowDataService.setVisible(false);

    // 3. Reset all slideshow data and state
    this.slideshowDataService.resetState();

    // 4. Reset slideshow state service
    this.slideshowStateService.resetSlideshow();

    // 5. Reset API integration service
    this.apiIntegrationService.reset();

    // 6. Reset file upload service to truly behave like page reload
    this.fileUploadService.reset();

    console.log('✅ Complete reset finished - ready for fresh analysis');
  }

  /**
   * Perform a results reset while preserving uploaded files
   * This makes re-matching clear previous results but keep user's uploaded files
   */
  private performResultsReset(): void {
    console.log('🔄 Performing results reset while preserving uploaded files...');

    // 1. Stop and cleanup audio completely
    this.audioService.stop(true); // Force stop with cleanup
    this.audioService.cleanup();

    // 2. Close any open slideshow modal immediately
    this.slideshowDataService.setVisible(false);

    // 3. Reset all slideshow data and state
    this.slideshowDataService.resetState();

    // 4. Reset slideshow state service
    this.slideshowStateService.resetSlideshow();

    // 5. Reset API integration service
    this.apiIntegrationService.reset();

    // 6. DO NOT reset file upload service - preserve user's uploaded files
    // this.fileUploadService.reset(); // <-- This line is intentionally removed

    console.log('✅ Results reset finished - uploaded files preserved, ready for fresh analysis');
  }
}
